import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { currentSeason, parseSeasonParam, seasonLabel } from "@shared/nba-time";

/**
 * Sélecteur global de saison (« voyage dans le temps »).
 * La saison est portée par l'URL (?saison=2015) pour que chaque vue soit partageable ;
 * à défaut, la dernière saison choisie est mémorisée localement, puis la saison en cours.
 */
interface SeasonCtx {
  season: number;
  current: number;
  isCurrent: boolean;
  label: string;
  setSeason: (s: number) => void;
}

const Ctx = createContext<SeasonCtx | null>(null);
const KEY = "ha-season";

function readStored(): number | null {
  try {
    return parseSeasonParam(localStorage.getItem(KEY));
  } catch {
    return null;
  }
}

export function SeasonProvider({ children }: { children: ReactNode }) {
  const [params, setParams] = useSearchParams();
  const current = currentSeason();
  const fromUrl = parseSeasonParam(params.get("saison"));
  const season = fromUrl ?? readStored() ?? current;

  const setSeason = useCallback(
    (s: number) => {
      try {
        if (s === current) localStorage.removeItem(KEY);
        else localStorage.setItem(KEY, String(s));
      } catch {
        /* stockage indisponible */
      }
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        if (s === current) next.delete("saison");
        else next.set("saison", String(s));
        return next;
      }, { replace: true });
    },
    [current, setParams],
  );

  const value = useMemo(
    () => ({ season, current, isCurrent: season === current, label: seasonLabel(season), setSeason }),
    [season, current, setSeason],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSeason(): SeasonCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSeason hors SeasonProvider");
  return v;
}

/** Années disponibles dans le sélecteur (1946-47 → saison en cours). */
export function seasonOptions(current: number): number[] {
  const out: number[] = [];
  for (let y = current; y >= 1946; y--) out.push(y);
  return out;
}
